 <!-- SOURCE: https://medium.com/tyrone-tudehope/composing-d3-visualizations-with-vue-js-c65084ccb686 -->
<template>
  <div
    :style="{height: height + 'px', width: width + 'px'}"
    class="multi-line-chart"
  >
    <svg
      v-if="isReady"
      :width="svgWidth"
      :height="svgHeight"
      @mousemove="mouseover"
    >
      <g :transform="`translate(${margin.left},${margin.top})`">
        <g
          :transform="`translate(0,${paddedHeight})`"
        >
          <axis-x
            :scale-x="scaleAxisX"
            :showTickText="true"
            :width="paddedWidth"
            :height="paddedHeight"
            :timeframe="timeframe"
            :showLabel="true"
            :type="labelTypeX"
          />

          <axis-x
            v-if="timeframe === 'ytd' && this.ytdRatio < 0.95"
            :scale-x="scaleXAddNow"
            :showTickText="true"
            :width="paddedWidth"
            :height="paddedHeight"
            :timeframe="timeframe"
            :showLabel="true"
            :type="'line'"
          />
        </g>

        <g v-if="scaleY && items">
          <axis-y
            :scale-y="scaleY"
            :ceil="ceil"
            :width="paddedWidth"
            :height="paddedHeight"
            :type="labelTypeY"
            :showLabel="showLabel"
          />
        </g>

        <g
          class="selector-wrapper"
        >
          <rect
            :x="selector.x"
            :y="0"
            :width="selectorWidth"
            :height="paddedHeight"
            class="selector"
            :class="{'d-none': !selector.visible}"
          />
        </g>

        <g
          v-if="legendItems"
          :style="{height: paddedHeight}"
        >
          <path
            v-for="(path, key) in paths"
            :id="key"
            :key="key"
            :index="key"
            :d="path['data']"
            :class="path['type']"
            :style="{stroke: getColor(key), strokeWidth: 2, fill: 'none', strokeDasharray: path['type'] == 'dashed' ? '2,2' : 'none'}"
          />
        </g>
      </g>
    </svg>

    <div
      v-else
      class="loading-wrapper"
      :style="{height: svgHeight + 'px', width: svgWidth + 'px'}"
    >
      <loading-icon
        :loading="!isReady"
        loading-class="text-center vertical-middle"
      />
    </div>

    <div
      v-if="isReady"
      :style="{left: popupX + 'px' }"
      class="popup"
      :class="{'d-none': !selector.visible}"
    >
      <div class="popup-title float-left">
        {{ selectorDates }}
      </div>
      <icon
        class="float-right"
        style="width: .85rem;height: .85rem;cursor:pointer;"
        icon="closedark"
        @click.native="closePopup"
      />
      <div :class="`categories ${Object.keys(selector.categories).length === 4 ? 'four-children' : ''}`">
        <div
          v-for="key in selCatArr"
          :key="key"
          :class="`category  ${key}`"
        >
          <span class="cat-text">
            {{ key in legendItems ? legendItems[key].name : key }}
          </span><span class="cat-value font-weight-bold">
            {{ selector.categories[key] }}
          </span>
        </div>
      </div>

      <div
        class="popup-tip"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import d3 from '@/analytics/importers/d3-importer'
import {capitalise} from '@/analytics/utils/data-analyser-utils'
import {getInterval} from '@/analytics/utils/keen-utils'
import {sortLegendKeys} from '@/analytics/helpers'

import ChartMixin from '@/analytics/mixins/ChartMixin'
import LineChartMixin from '@/analytics/mixins/LineChartMixin'

import AxisX from './AxisX'
import AxisY from './AxisY'

const props = {
  legendItems: {
    type: Object,
    required: true,
  },
  margin: {
    type: Object,
    default: () => ({
      left: 70,
      right: 85,
      top: 10,
      bottom: 70,
    }),
  },
  timeframe: {
    type: String,
    required: false,
    default: 'this_30_days',
  },
  showLabel: {
    type: Boolean,
    default: true,
  },
  timeframeLabels: {
    type: Array,
    required: false,
    default: [],
  },
  width: {
    type: Number,
    required: true,
  },
  height: {
    type: Number,
    required: true,
  },
  minWidth: {
    type: Number,
    required: true,
  },
  dataType: {
    type: String,
    default: null,
  },
  analyticsType: {
    type: String,
    default: null,
  },
}

const defaultSelector = {
  visible: false,
  x: 0,
  width: 0,
  title: 'interval',
  categories: {
    emailReads: 0,
    web: 0,
  },
}

export default {
  name: 'MultiLineChart',
  components: {
    AxisX,
    AxisY,
  },
  mixins: [ChartMixin, LineChartMixin],
  props: props,
  data () {
    return {
      paths: {},
      points: {},
      lastHoverPoint: {},
      selectorWhitelist: ['emailReads', 'web', 'article', 'newsletter', 'analyst', 'channel', 'emailReadsChannel', 'webChannel', 'network', 'emailReadsNetwork', 'webNetwork','webViews'],
      selector: defaultSelector,
    }
  },
  computed: {
    svgWidth () {
      return Math.max(this.minWidth, this.width)
    },
    svgHeight () {
      return this.height
    },
    paddedWidth () {
      return this.svgWidth - this.margin.left - this.margin.right
    },
    paddedHeight () {
      return this.svgHeight - this.margin.top - this.margin.bottom
    },
    ceil () {
      const maxY = Math.max.apply(Math, Object.entries(this.items).map((it) => Math.max(...it[1])), 10)

      return Math.max(5, Math.ceil(maxY + maxY * 0.2))
    },
    scaleX () {
      return d3.scalePoint()
          .range([0, this.paddedWidth])
          .domain(this.intervalsX())
    },
    scaleXAddNow () {
      return d3.scalePoint()
          .range([0, this.paddedWidth])
          .domain(['', 'Today'])
    },
    scaleY () {
      return d3.scaleLinear()
          .rangeRound([this.paddedHeight, 0])
          .domain([0, this.ceil])
          .nice(6)
    },
    ytdRatio () {
      const week = this.firstItems.length
      const months = Math.floor(week/4.348)

      return Math.min(1, moment().month(months).startOf('month').dayOfYear() / moment().week(week).startOf('week').dayOfYear())
    },
    scaleAxisX () {
      let range = [0, this.paddedWidth]

      if (this.timeframe === 'ytd') {
        range = [0, this.paddedWidth * this.ytdRatio]
      } else if (!this.upToOneWeek && this.upToOneMonth) {
        range = [this.paddedWidth / (this.steps-1) * ((this.steps-1) % 7), this.paddedWidth]
      } else if (this.upToHalfYear) {
        range = [[5, 13].includes(this.steps) ? 0 : this.paddedWidth / (this.steps-1) * ((this.steps-1) % 4.348), this.paddedWidth]
      } else if (this.upToOneYear) {
        range = [this.steps === 5 ? 0 : this.paddedWidth / (this.steps-1) * ((this.steps-1) % (4.348*3)), this.paddedWidth]
      } else if (this.upToTenYears) {
        range = [this.paddedWidth / (this.steps - 1) * ((this.steps - 1) % 12), this.paddedWidth]
      }

      return d3.scalePoint()
          .range(range)
          .domain(this.intervalsX('axis'))
    },
    scaleXStep () {
      return this.scaleX.step()
    },
    selectorPoints () {
      return this.points[Object.keys(this.points)[0]]
    },
    firstItems () {
      return this.items ? this.items[Object.keys(this.items)[0]] : null
    },
    labelTypeX () {
      if (this.upToOneWeek) {
        return 'line'
      } else if (this.upToOneMonth) {
        return 'vertGridFirst'
      } else if (['this_5_weeks', 'this_13_weeks'].includes(this.timeframe)) {
        return 'vertGrid'
      } else if (this.timeframe === 'ytd') {
        return this.inFirstMonth ? 'vertGrid' : 'vertGridLast'
      } else if (this.upToOneYear) {
        return 'vertGridFirst'
      } else {
        return 'vertGridFirst'
      }
    },
    labelTypeY () {
      return 'line'
    },
    selectorWidth () {
      const index = this.selector.index
      if (!this.firstItems) {
        return 0
      }
      if (this.firstItems.length > 1 && (index === 0 || index === this.firstItems.length - 1)) {
        return this.scaleXStep / 2
      }

      return this.scaleXStep
    },
    popupX () {
      if (!this.firstItems) {
        return 0
      }
      const index = this.selector.index
      let offsetDivi = 2
      if (this.firstItems.length > 1 && (index === 0 || index === this.firstItems.length - 1)) {
        offsetDivi = 4
      }

      return this.selector.x + this.scaleXStep / offsetDivi - 20
    },
    interval () {

      return getInterval(this.timeframe)
    },
    isReady () {
      return (this.items) != null
    },
    inFirstMonth () {
      return (this.firstItems?.length || 0) <= 5
    },
    selectorDates () {
  if (Array.isArray(this.timeframeLabels) && this.timeframeLabels.length) {
    const idx = this.selector.index;
    return this.timeframeLabels[idx] || '';
  }
      if (this.upToOneWeek) {
        return 7 - this.selector.index - 1 === 0 ? "Today" : `${moment().subtract(7 - this.selector.index - 1, 'day').format('dddd')}`
      } else if (this.upToOneMonth) {
        return this.steps - this.selector.index - 1 === 0 ?  "Today" : `${moment().subtract(this.steps - this.selector.index - 1, 'day').format('D MMMM YYYY')}`
      } else if (this.timeframe === 'ytd') {
        const week = moment().week(this.selector.index + 1)
        return parseInt(moment().format('w')) - this.selector.index - 1 === 0 ? `${week.day('Sunday').format('D MMM')} - Today` : `${week.day('Sunday').format('D MMM')} - ${week.day('Saturday').format('D MMM YY')}`

      } else if (this.upToOneYear) {
        const week = moment().week(parseInt(moment().format('w')) - (this.steps - this.selector.index - 1))
        if (this.steps - this.selector.index - 1 === 0) {
          return `${week.day('Sunday').format('D MMM')} - Today`
        }
        return `${week.day('Sunday').format('D MMM')} - ${week.day('Saturday').format('D MMM YY')}`
      } else if (this.upToTenYears) {
        return `${moment().subtract(this.steps - this.selector.index - 1, 'month').format('MMMM YYYY')}`
      } else {
        return this.interval
      }
    },
    selCatArr () {
      return Object.keys(this.selector.categories).sort(sortLegendKeys)
    },
    steps () {
      return parseInt(this.timeframe.split('_')[1])
    },
    upToOneWeek () {
      return this.timeframe.includes('days') && this.steps < 8
    },
    upToOneMonth () {
      return this.timeframe.includes('days') && this.steps < 31
    },
    upToHalfYear () {
      return this.timeframe.includes('weeks') && this.steps < 28
    },
    upToOneYear () {
      return this.timeframe.includes('weeks') && this.steps < 53
    },
    upToTenYears () {
      return this.timeframe.includes('months') && this.steps < 121
    },
  },
  methods: {
    intervalsX (type = 'points') {
      if (!this.firstItems) {
        return []
      }
  if (Array.isArray(this.timeframeLabels) && this.timeframeLabels.length) {
    return this.timeframeLabels;
  }

      if (type === 'axis') {
        if (this.timeframe === 'ytd') {
          if (this.inFirstMonth) {
            return ['Jan', "Today"]
          } else {
            const months = Math.ceil(this.firstItems.length/4.348)
            const monthsLabels = [...[...Array(months-1)].map((x, i) => moment().month(i).format('MMM')), "Today"]

            return monthsLabels
          }
        } else if (this.upToOneWeek) {
          const response = [...([...Array(6)].map((x, i) => `${moment().subtract(i +1, 'day').format('ddd')}_${i + 1}`).reverse()), "Today"]
          return response
        } else if (this.upToOneMonth) {
          const weeks = Math.ceil(this.steps / 7)
          return [...([...Array(weeks - 1)].map((x, i) => moment().subtract((i + 1) * 7, 'day').format('D MMM')).reverse()), "Today"]
        } else if (this.upToHalfYear) {
          const months = Math.ceil((this.steps - 1) / 4.348 + ([5, 13].includes(this.steps) ? 1 : 0))
          return [...Array(months)].map((x, i) => i === 0 ? 'Today' : i === 1 ? '1 Month Ago' : `${i} Months Ago`).reverse()
        } else if (this.upToOneYear) {
          const quarters = Math.ceil((this.steps - 1) / (4.348 * 3))
          return [...Array(quarters)].map((x, i) => i === 0 ? 'Today' : `${i * 3} Months Ago`).reverse()
        } else if (this.upToTenYears) {
          const years = Math.ceil(this.steps / 12)
          return [...Array(years)].map((x, i) => i === 0 ? 'Today' : i === 1 ? '1 Year Ago' : `${i} Years Ago`).reverse()
        }
      }

      return this.firstItems.map((x, i) => i)
    },
    createArea: d3.area().x((d) => d.x).y0((d) => d.max).y1((d) => d.y),
    createLine: d3.line().x((d) => d.x).y((d) => d.y),
    createValueSelector: d3.area().x0((d) => d.x).y0((d) => d.max).y1(24),
    drawChart () {
      if (!this.items || !this.timeframeLabels.length) {
        return
      }

      this.paths = {}
      this.points = {}
      this.selector = defaultSelector

      for (const [prop, its] of Object.entries(this.items).reverse()) {
        const locPoints = []
        if (its[0] === undefined || prop.length === 0) {
          continue;
        }


        if (this.firstItems.length === 1) {
          locPoints.push({
            x: 0,
            y: this.paddedHeight,
            max: this.paddedHeight,
          })
        }

        for (const [i, d] of its.entries()) {
          locPoints.push({
            x: (this.timeframeLabels ? this.scaleX(this.timeframeLabels[i]) : this.scaleX(i)) * (this.firstItems.length === 1 ? 2 : 1),
            y: this.scaleY(d),
            max: this.paddedHeight,
          })
        }

        const pathPoints = locPoints.slice().map((p) => ({...p}))

        if (this.showNa([prop, this.dataType], this.timeframe)) {
          const len = locPoints.length
          const naPoints = []

          naPoints.push({...locPoints[len-1]})
          naPoints.push({...locPoints[len-2]})
          naPoints[0].y = naPoints[1].y

          locPoints.pop()
          this.$set(this.paths, prop + 'Na', {type: 'dashed', prop: prop + 'Na', data: this.createLine(naPoints)})
        }


        this.$set(this.points, prop, pathPoints)
        this.$set(this.paths, prop, {type: 'line', prop: prop, data: this.createLine(locPoints)})

        // this.$set(this.paths, 'area', this.createArea(this.points))
      }
    },
    mouseover (params) {
      if (this.selectorPoints.length > 0) {
        const x = params.offsetX - this.margin.left
        const closestPoint = this.getClosestPoint(x)
        const ind = this.firstItems.length > 1 ? closestPoint.index : 0
        if (this.lastHoverPoint.index !== ind) {
          const point = this.selectorPoints[ind]
          const sel = {
            visible: true,
            x: ind === 0 ? 0 : point.x - this.scaleXStep / 2,
            title: this.scaleX.domain()[ind],
            index: ind,
            categories: {},
          }

          for (const prop in this.items) {
            if (this.selectorWhitelist.includes(prop)) {
              sel.categories[prop] = this.showNa([prop, this.dataType], this.timeframe) && ind === this.items[prop].length - 1
                ? 'n/a'
                : this.items[prop][ind]
            }
          }

          this.selector = sel
          this.lastHoverPoint = closestPoint
        }
      }
    },
    getClosestPoint (x) {
      const closest = this.selectorPoints
          .map((point, index) => ({
            x: point.x,
            diff: Math.abs(point.x - x),
            index,
          }))
          .reduce((memo, val) => (memo.diff < val.diff ? memo : val))

      return closest
    },
    closePopup () {
      this.$set(this.selector, 'visible', false)
    },
    capitalise (str) {
      return capitalise(str)
    },
  },
}
</script>

<style lang="scss">
  .multi-line-chart {
    position: relative;
    overflow: hidden;
    overflow-x: auto;

    g.dashed {
      stroke-dasharray: 3, 2;
    }
  }


</style>
